import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Card, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format, isValid, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useImage } from '@pflegenavi/frontend/api-nursing-home';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import {
  pflegenaviBase64Logo,
  usePrintPdfFinanceOverviewAtDate,
} from '@pflegenavi/frontend/pdf-export';
import {
  Button,
  Modal,
  ModalInner,
  useErrorSnackbar,
} from '@pflegenavi/web-components';
import { makeSafeFilename } from '@pflegenavi/shared/utils';
import { useInstitutionCashListAtDate } from '../hooks/useInstitutionCashListAtDate';

interface FinanceOverviewPdfModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const FinanceOverviewPdfModal: FC<FinanceOverviewPdfModalProps> = ({
  open,
  setOpen,
}) => {
  const { t } = useTranslation();
  const { selectedNursingHome } = useNursingHomeContext();
  const { data: pdfLogo } = useImage(selectedNursingHome?.pdfLogoId);
  const { institutionCashList, fetchInstitutionCashListAtDate } =
    useInstitutionCashListAtDate();
  const { handlePrintPdfFinanceOverviewAtDate } =
    usePrintPdfFinanceOverviewAtDate();
  const { enqueueErrorSnackbar } = useErrorSnackbar();

  const [date, setDate] = useState<Date | null>(() => new Date());
  const [isGenerating, setIsGenerating] = useState(false);

  const handleClose = useCallback(() => {
    setOpen(false);
    setDate(new Date());
  }, [setOpen]);

  const dateError = useMemo(() => {
    if (date && startOfDay(date) > startOfDay(new Date())) {
      return t('accounting.dashboard.csv-export.modal.date-in-future-error');
    }
    return undefined;
  }, [date, t]);

  const handleDownload = useCallback(async () => {
    if (!date || !selectedNursingHome) {
      return;
    }
    setIsGenerating(true);
    try {
      const cashList = await fetchInstitutionCashListAtDate(date);
      const pdf = await handlePrintPdfFinanceOverviewAtDate({
        nursingHomeName: selectedNursingHome.name,
        date,
        logo: pdfLogo ? pdfLogo.url : pflegenaviBase64Logo,
        cashList,
      });
      pdf.save(
        makeSafeFilename(
          t('accounting.report.pdf-name', {
            nursingHomeName: selectedNursingHome.name,
            date: format(date, 'yyyy-MM-dd'),
          })
        )
      );
      handleClose();
    } catch (error) {
      enqueueErrorSnackbar(error, t('errors.something-went-wrong'), {
        variant: 'error',
      });
    } finally {
      setIsGenerating(false);
    }
  }, [
    date,
    enqueueErrorSnackbar,
    fetchInstitutionCashListAtDate,
    handleClose,
    handlePrintPdfFinanceOverviewAtDate,
    pdfLogo,
    selectedNursingHome,
    t,
  ]);

  return (
    <Modal open={open} handleClose={handleClose}>
      <ModalInner>
        <Box>
          <Card sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {t('accounting.dashboard.download-button.overview-pdf')}
            </Typography>
            {institutionCashList ? (
              <>
                <Typography sx={{ mb: 2 }}>
                  {t('accounting.report.date-modal.description')}
                </Typography>
                <DatePicker
                  label={t('accounting.report.date')}
                  value={date}
                  onChange={(newValue) =>
                    setDate(newValue && isValid(newValue) ? newValue : null)
                  }
                  maxDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: dateError,
                      error: !!dateError,
                    },
                  }}
                />
              </>
            ) : (
              <Alert severity="info">
                {t('accounting.report.date-modal.no-institution-cash-list')}
              </Alert>
            )}
            <Stack
              direction="row"
              sx={{ justifyContent: 'flex-end', gap: 1, mt: 3 }}
            >
              <Button
                variant="outlined"
                disabled={isGenerating}
                onClick={handleClose}
              >
                {t('actions.cancel')}
              </Button>
              <Button
                color="primary"
                variant="contained"
                loading={isGenerating}
                disabled={!institutionCashList || !date || Boolean(dateError)}
                onClick={handleDownload}
              >
                {t('accounting.report.date-modal.download')}
              </Button>
            </Stack>
          </Card>
        </Box>
      </ModalInner>
    </Modal>
  );
};
